import React, { useEffect, useRef, useContext } from 'react';
import { MapContext } from '@agissc/ktag-map';
import { Coordinate } from 'ol/coordinate';
import './identifySpotlightOverlay.scss';

interface IdentifyCanvasOverlayProps {
  identifyCoordinate: Coordinate;
  tolerance: number;
}

const IdentifyCanvasOverlay = ({
  identifyCoordinate,
  tolerance,
}: IdentifyCanvasOverlayProps) => {
  const { map, view } = useContext(MapContext);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const updateCanvas = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !map || !view || !img || !img.complete) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const size = map.getSize();
    if (!size) return;

    const [width, height] = size;
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);

    const center = map.getPixelFromCoordinate(identifyCoordinate);
    if (!center) return;

    const resolution = view.getResolution();
    if (!resolution) return;

    const radiusInPixels = tolerance / resolution;

    context.beginPath();
    context.arc(center[0], center[1], radiusInPixels, 0, 2 * Math.PI);
    context.fillStyle = 'rgb(0, 122, 184, 0.2)';
    context.fill();

    context.strokeStyle = 'rgba(255, 255, 255, 1)';
    context.lineWidth = 5;
    context.shadowColor = 'rgb(0, 0, 0, 0.3)';
    context.shadowBlur = 15;
    context.stroke();

    // draw the image
    const imgWidth = 48;
    const imgHeight = 48;
    const offsetY = 24;
    context.drawImage(
      img,
      center[0] - imgWidth / 2,
      center[1] - imgHeight / 2 - offsetY,
      imgWidth,
      imgHeight,
    );
  };

  useEffect(() => {
    if (!map || !view) return undefined;

    const currentImg = imgRef.current;

    const initializeOverlay = () => {
      map.on('postrender', updateCanvas);
      updateCanvas();
    };

    if (!currentImg || !currentImg.complete || currentImg.naturalWidth === 0) {
      const newImg = new Image();
      newImg.src = 'assets/images/location-blue.svg';
      newImg.onload = initializeOverlay;

      imgRef.current = newImg;

      return undefined;
    }

    initializeOverlay();

    return () => {
      map.un('postrender', updateCanvas);
    };
  }, [map, view, identifyCoordinate, tolerance]);

  return <canvas ref={canvasRef} className="spotlight-overlay" />;
};

export default IdentifyCanvasOverlay;
